import React, { useState, useEffect, useContext } from "react";

import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	List,
	ListItem,
	ListItemIcon,
	ListItemText,
	ListSubheader,
	Switch,
	Divider,
	FormControl,
	RadioGroup,
	FormControlLabel,
	Radio,
	Slider,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import NotificationsActiveIcon from "@material-ui/icons/NotificationsActive";
import VolumeUpIcon from "@material-ui/icons/VolumeUp";
import Brightness4Icon from "@material-ui/icons/Brightness4";
import FormatSizeIcon from "@material-ui/icons/FormatSize";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { BotaoCancelar } from "../BotoesModal";
import {
	ColorModeContext,
	FONT_SIZE_MIN,
	FONT_SIZE_MAX,
	FONT_SIZE_STEP,
	FONT_SIZE_DEFAULT,
} from "../../context/Theme/ColorModeContext";

const useStyles = makeStyles(theme => ({
	scopeBox: {
		padding: theme.spacing(1, 2, 2, 2),
	},
	fontSizeSlider: {
		width: 140,
		marginRight: theme.spacing(1),
	},
}));

// Marcas do slider de tamanho de fonte (8, 10, ..., 22).
const FONT_SIZE_MARKS = Array.from(
	{ length: (FONT_SIZE_MAX - FONT_SIZE_MIN) / FONT_SIZE_STEP + 1 },
	(_, i) => {
		const v = FONT_SIZE_MIN + i * FONT_SIZE_STEP;
		return { value: v, label: String(v) };
	}
);

// Modal com as preferências de notificação por usuário (persistidas no banco).
// Mesma lógica que existia na SettingsSidebar — agora acionada pelo botão de
// configuração dentro do dropdown de filtros (TicketsManager).
const NotificationSettingsModal = ({ open, onClose }) => {
	const classes = useStyles();
	const { user } = useContext(AuthContext);
	// Setters do tema para aplicar instantaneamente (antes do round-trip ao banco).
	const { setDarkMode: applyDarkMode, setFontSize: applyFontSize } = useContext(
		ColorModeContext
	);
	const isGestor = !!user?.gestor;

	const [saving, setSaving] = useState(false);

	// Estados locais (otimistas) derivados do usuário.
	const [desktop, setDesktop] = useState(user?.notificacoesDesktop !== false);
	const [som, setSom] = useState(user?.notificacoesSom === true);
	const [escopo, setEscopo] = useState(user?.notificacoesEscopo || "minhas");
	const [darkMode, setDarkMode] = useState(user?.darkMode === true);
	const [fontSize, setFontSize] = useState(
		user?.fontSizeMensagens || FONT_SIZE_DEFAULT
	);

	// Mantém em sincronia quando o objeto user é atualizado (ex.: socket "user").
	useEffect(() => {
		setDesktop(user?.notificacoesDesktop !== false);
	}, [user?.notificacoesDesktop]);
	useEffect(() => {
		setSom(user?.notificacoesSom === true);
	}, [user?.notificacoesSom]);
	useEffect(() => {
		setEscopo(user?.notificacoesEscopo || "minhas");
	}, [user?.notificacoesEscopo]);
	useEffect(() => {
		setDarkMode(user?.darkMode === true);
	}, [user?.darkMode]);
	useEffect(() => {
		setFontSize(user?.fontSizeMensagens || FONT_SIZE_DEFAULT);
	}, [user?.fontSizeMensagens]);

	// Persiste um patch parcial, com atualização otimista e rollback em erro.
	const persist = async (patch, applyOptimistic, rollback) => {
		applyOptimistic();
		setSaving(true);
		try {
			await api.put("/users/notificacoes", patch);
		} catch (err) {
			if (rollback) rollback();
			toastError(err);
		} finally {
			setSaving(false);
		}
	};

	const handleDesktop = () => {
		const novo = !desktop;
		persist(
			{ notificacoesDesktop: novo },
			() => setDesktop(novo),
			() => setDesktop(!novo)
		);
		// Ao habilitar, pede permissão dentro do gesto do usuário (clique).
		if (
			novo &&
			"Notification" in window &&
			Notification.permission === "default"
		) {
			Notification.requestPermission();
		}
	};

	const handleSom = () => {
		const novo = !som;
		persist(
			{ notificacoesSom: novo },
			() => setSom(novo),
			() => setSom(!novo)
		);
	};

	const handleEscopo = e => {
		const novo = e.target.value;
		const anterior = escopo;
		persist(
			{ notificacoesEscopo: novo },
			() => setEscopo(novo),
			() => setEscopo(anterior)
		);
	};

	// Alterna o tema: aplica instantaneamente (ColorModeContext) e persiste no
	// banco (Users.darkMode). Em erro, faz rollback do estado e do tema aplicado.
	const handleDarkMode = () => {
		const novo = !darkMode;
		persist(
			{ darkMode: novo },
			() => {
				setDarkMode(novo);
				applyDarkMode(novo);
			},
			() => {
				setDarkMode(!novo);
				applyDarkMode(!novo);
			}
		);
	};

	// Preview ao vivo enquanto arrasta o slider: aplica a variável CSS na hora,
	// sem bater no banco. A persistência acontece só ao soltar (handleFontSize).
	const handleFontSizePreview = (_e, value) => {
		const novo = Number(Array.isArray(value) ? value[0] : value);
		setFontSize(novo);
		applyFontSize(novo);
	};

	// Persiste o tamanho da fonte no banco (Users.fontSizeMensagens) ao soltar o
	// slider. Já está aplicado visualmente pelo preview; em erro faz rollback.
	const handleFontSize = (_e, value) => {
		const novo = Number(Array.isArray(value) ? value[0] : value);
		const anterior = user?.fontSizeMensagens || FONT_SIZE_DEFAULT;
		if (novo === anterior) return;
		persist(
			{ fontSizeMensagens: novo },
			() => {
				setFontSize(novo);
				applyFontSize(novo);
			},
			() => {
				setFontSize(anterior);
				applyFontSize(anterior);
			}
		);
	};

	return (
		<Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth scroll="paper">
			<DialogTitle>{i18n.t("settingsSidebar.title")}</DialogTitle>
			<DialogContent dividers>
				<List
					dense
					subheader={
						<ListSubheader disableSticky>
							{i18n.t("settingsSidebar.notificationsSection")}
						</ListSubheader>
					}
				>
					<ListItem>
						<ListItemIcon>
							<NotificationsActiveIcon />
						</ListItemIcon>
						<ListItemText primary={i18n.t("settingsSidebar.enableAll")} />
						<Switch
							edge="end"
							color="primary"
							checked={desktop}
							disabled={saving}
							onChange={handleDesktop}
							inputProps={{
								"aria-label": i18n.t("settingsSidebar.enableAll"),
							}}
						/>
					</ListItem>

					<ListItem>
						<ListItemIcon>
							<VolumeUpIcon />
						</ListItemIcon>
						<ListItemText primary={i18n.t("settingsSidebar.enableSound")} />
						<Switch
							edge="end"
							color="primary"
							checked={som}
							disabled={saving || !desktop}
							onChange={handleSom}
							inputProps={{
								"aria-label": i18n.t("settingsSidebar.enableSound"),
							}}
						/>
					</ListItem>
				</List>

				{isGestor && (
					<>
						<Divider />
						<List
							dense
							subheader={
								<ListSubheader disableSticky>
									{i18n.t("settingsSidebar.gestorSection")}
								</ListSubheader>
							}
						>
							<FormControl
								component="fieldset"
								className={classes.scopeBox}
								disabled={saving || !desktop}
							>
								<RadioGroup value={escopo} onChange={handleEscopo}>
									<FormControlLabel
										value="todas"
										control={<Radio color="primary" />}
										label={i18n.t("settingsSidebar.scopeAll")}
									/>
									<FormControlLabel
										value="minhas"
										control={<Radio color="primary" />}
										label={i18n.t("settingsSidebar.scopeMine")}
									/>
								</RadioGroup>
							</FormControl>
						</List>
					</>
				)}

				<Divider />

				<List
					dense
					subheader={
						<ListSubheader disableSticky>
							{i18n.t("settingsSidebar.appearanceSection")}
						</ListSubheader>
					}
				>
					<ListItem>
						<ListItemIcon>
							<Brightness4Icon />
						</ListItemIcon>
						<ListItemText primary={i18n.t("settingsSidebar.darkMode")} />
						<Switch
							edge="end"
							color="primary"
							checked={darkMode}
							disabled={saving}
							onChange={handleDarkMode}
							inputProps={{
								"aria-label": i18n.t("settingsSidebar.darkMode"),
							}}
						/>
					</ListItem>

					<ListItem>
						<ListItemIcon>
							<FormatSizeIcon />
						</ListItemIcon>
						<ListItemText
							primary={i18n.t("settingsSidebar.fontSizeMensagens")}
							secondary={`${fontSize}px`}
						/>
						<Slider
							className={classes.fontSizeSlider}
							value={fontSize}
							min={FONT_SIZE_MIN}
							max={FONT_SIZE_MAX}
							step={FONT_SIZE_STEP}
							marks={FONT_SIZE_MARKS}
							disabled={saving}
							valueLabelDisplay="auto"
							onChange={handleFontSizePreview}
							onChangeCommitted={handleFontSize}
							aria-label={i18n.t("settingsSidebar.fontSizeMensagens")}
						/>
					</ListItem>
				</List>
			</DialogContent>
			<DialogActions>
				<BotaoCancelar onClick={onClose}>
					{i18n.t("settingsSidebar.close") || "Fechar"}
				</BotaoCancelar>
			</DialogActions>
		</Dialog>
	);
};

export default NotificationSettingsModal;
